<template>
  <div :class="['fleet-assistant-root', { 'is-embedded': embedded }]">
    <div v-if="!embedded" style="width: 100%; background-color: #eee; border: 1px dotted grey; margin-bottom: 25px;">
      <span style="font-size: 26px; font-weight: bold">Fleet Assistant</span><br/>
      <div style="font-style: italic; font-size: 18px; padding-bottom: 15px; margin-right: 10px">
        Ask questions about your fleet, navigation, or get help with the system
      </div>
    </div>

    <div class="fleet-assistant-shell">
      <!-- chat wrapper -->
      <div class="fleet-assistant-chat-wrapper">
        <!-- msgs — the welcome empty-state sits at the TOP (its own margin-top
             controls spacing). actual messages + thinking indicator live
             inside the `.fa-messages-anchor` wrapper, which uses margin-top:
             auto to push them to the BOTTOM of the scroll viewport (so the
             latest message hugs the input instead of floating at the top with
             a gap). when content overflows, the auto margin collapses to 0
             and normal scroll takes over. -->
        <div ref="messagesContainer" class="fa-messages-scroll">
          <div v-if="sessionLoaded && messages.length === 0" class="fa-welcome-empty">
            <p class="fa-welcome-title">Welcome! I'm your Fleet Assistant. How can I help you today?</p>
            <p class="fa-welcome-sub">You can ask me about:</p>
            <ul class="fa-welcome-list">
              <li>Navigation and how to use features</li>
              <li>Your current page and what you're seeing</li>
              <li>Fleet data, vehicles, drivers, and reports</li>
              <li>General questions about the system</li>
            </ul>
            <div class="fa-suggested-prompts">
              <button
                v-for="prompt in suggestedPrompts"
                :key="prompt"
                type="button"
                class="fa-prompt-chip"
                @click="sendSuggestedPrompt(prompt)"
              >{{ prompt }}</button>
            </div>
          </div>
          <div class="fa-messages-anchor">
          <div v-for="(message, index) in messages" :key="index" style="margin-bottom: 15px;">
            <div v-if="message.role === 'user'" style="text-align: right;">
              <div style="display: inline-block; background-color: #007ac7; color: white; padding: 10px 15px; border-radius: 18px; max-width: 70%; word-wrap: break-word;">
                {{ message.content }}
              </div>
              <div v-if="showTimestamps && message.timestamp" class="message-timestamp" style="text-align: right;" :title="formatTimestampFull(message.timestamp)">
                {{ formatTimestamp(message.timestamp) }}
              </div>
            </div>
            <div v-else style="text-align: left;">
              <!-- over-limit callout replaces the normal bubble when AI budget is exhausted.
                   wrapped in .fa-assistant-msg-wrap so it gets the same chat-bubble sizing
                   as regular assistant messages, with the timestamp living OUTSIDE the bubble. -->
              <div v-if="message.over_limit" class="fa-assistant-msg-wrap">
                <div class="fa-limit-callout">
                  <div class="callout-head">
                    <!-- anchor icon: full parking-sign P — same design as help_center callout.
                         outer rounded square + thin white inset border + white P letter. -->
                    <svg class="callout-anchor-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                      <rect x="1" y="1" width="14" height="14" rx="2.5" ry="2.5" fill="currentColor"/>
                      <rect x="2.4" y="2.4" width="11.2" height="11.2" rx="1.6" ry="1.6" fill="none" stroke="#fff" stroke-width="0.7"/>
                      <path fill="#fff" fill-rule="evenodd" d="M5.5 4.5v7H7V9h1.5a2.25 2.25 0 0 0 0-4.5H5.5Zm1.5 1.5v1.75h1.5a0.875 0.875 0 0 0 0-1.75H7Z"/>
                    </svg>
                    <span class="fa-badge disabled">Fleet Assistant Paused</span>
                  </div>
                  <div class="callout-body">
                    <p>{{ message.content }}</p>
                    <div class="fa-limit-callout-action">
                      <button type="button" class="callout-cta-btn" @click="goToUsageRequest">
                        Request Additional Usage
                      </button>
                    </div>
                  </div>
                </div>
                <!-- timestamp lives outside the bubble, mirroring the regular .fa-meta-row pattern -->
                <div v-if="showTimestamps && message.timestamp" class="fa-meta-row">
                  <span class="message-timestamp" :title="formatTimestampFull(message.timestamp)">
                    {{ formatTimestamp(message.timestamp) }}
                  </span>
                </div>
              </div>
              <div v-else class="fa-assistant-msg-wrap">
                <div style="background-color: #e9ecef; color: #333; padding-top: 10px; padding-left: 15px; padding-right: 15px; border-radius: 18px; word-wrap: break-word;" class="markdown-content" v-html="renderMarkdown(message.content)"></div>
                <!-- timestamp + rating on same line -->
                <div class="fa-meta-row">
                <span v-if="showTimestamps && message.timestamp" class="message-timestamp" :title="formatTimestampFull(message.timestamp)">
                  {{ formatTimestamp(message.timestamp) }}
                </span>
                <span v-if="!loading" class="fa-rating-controls">
                  <button type="button" class="fa-rate-btn" :class="{ active: messageRatings[index] === 'positive' }" @click="rateResponse(index, 'positive')" title="Helpful">
                    <i :class="messageRatings[index] === 'positive' ? 'fa fa-thumbs-up' : 'fa fa-thumbs-o-up'"></i>
                  </button>
                  <button type="button" class="fa-rate-btn" :class="{ active: messageRatings[index] === 'negative' }" @click="rateResponse(index, 'negative')" title="Not helpful">
                    <i :class="messageRatings[index] === 'negative' ? 'fa fa-thumbs-down' : 'fa fa-thumbs-o-down'"></i>
                  </button>
                  <button type="button" class="fa-rate-btn concern" :class="{ active: messageRatings[index] === 'concern' }" @click="rateResponse(index, 'concern')" title="Report a concern">
                    <i :class="messageRatings[index] === 'concern' ? 'fa fa-flag' : 'fa fa-flag-o'"></i>
                  </button>
                  <span v-if="messageRatingsConfirmed[index]" class="fa-rated-thanks"><i class="fa fa-check"></i> Thanks</span>
                </span>
              </div>
              <!-- continue link for truncated responses -->
              <div v-if="message === messages[messages.length - 1] && message.is_truncated && !loading" style="margin-top: 4px; margin-left: 4px;">
                <a
                  href="#"
                  @click.prevent="continueResponse"
                  style="color: #007bff; text-decoration: underline; cursor: pointer; font-size: 14px;"
                  :class="{ 'text-muted': loading }"
                >
                  Continue →
                </a>
              </div>
              <!-- rating comment (expands below when negative/concern) -->
              <div v-if="ratingCommentIndex === index" class="fa-rating-comment">
                <b-form-textarea
                  ref="ratingCommentInput"
                  v-model="ratingComment"
                  placeholder="What went wrong? (optional)"
                  rows="2"
                  max-rows="3"
                  size="sm"
                  style="font-size: 13px;"
                ></b-form-textarea>
                <div style="margin-top: 4px;">
                  <b-button size="sm" variant="primary" @click="submitRatingComment(index)" :disabled="ratingSubmitting">Submit</b-button>
                  <b-button size="sm" variant="outline-secondary" class="ml-1" @click="skipRatingComment(index)">Skip</b-button>
                </div>
              </div>
              </div>
            </div>
          </div>
          <div v-if="showThinking" style="text-align: left; margin-top: 10px;">
            <div class="thinking-indicator">
              <component :is="currentAnimationComponent" />
            </div>
          </div>
          </div>
        </div>

        <!-- hidden file picker -->
        <input
          v-if="userDocUploadsEnabled"
          ref="faDocPicker"
          type="file"
          multiple
          accept=".pdf,.docx,.txt"
          style="display:none"
          @change="onDocPickerChange"
        />

        <!-- input -->
        <div style="border-top: 1px solid #ccc; padding: 15px; background-color: white;">
          <div v-if="sessionDocsExpanded && userDocUploadsEnabled" class="fa-session-docs">
            <div class="fa-session-docs-row">
              <b-button size="sm" variant="primary" @click="openDocPicker" :disabled="deletingDocuments">
                Add Document(s)
              </b-button>
              <b-button
                v-if="documentCount > 1"
                size="sm"
                variant="outline-danger"
                @click="deleteAllDocuments"
                :disabled="uploadingDocuments || deletingDocuments"
                title="Remove all documents"
              >
                <b-spinner v-if="deletingDocuments" small class="mr-1" />
                {{ deletingDocuments ? 'Deleting...' : 'Delete All Documents' }}
              </b-button>
              <span class="fa-session-docs-spacer"></span>
              <b-button
                v-if="isAdmin"
                size="sm"
                variant="outline-success"
                @click="clearSession"
                :disabled="loading || messages.length === 0"
                title="Clear conversation and start fresh"
              >
                Start New Chat
              </b-button>
            </div>
            <div v-if="uploadItems.length" class="fa-session-docs-list">
              <div
                v-for="item in uploadItems"
                :key="item.id"
                class="fa-attachment-pill"
                :class="'status-' + item.status"
                :title="item.error || ''"
              >
                <span class="fa-attachment-icon" aria-hidden="true">
                  <span v-if="item.status === 'failed'" class="fa-attachment-failed-icon">!</span>
                  <span v-else class="fa-attachment-file-icon" :class="'file-' + docFileType(item)">
                    <svg v-if="docFileType(item) === 'pdf'" viewBox="0 0 24 24" class="fa-attachment-logo pdf" aria-hidden="true">
                      <rect x="2" y="3" width="20" height="18" rx="3" fill="currentColor"></rect>
                      <text x="12" y="13" text-anchor="middle" dominant-baseline="middle" font-size="8.5" font-family="Arial, sans-serif" font-weight="700" fill="#fff">PDF</text>
                    </svg>
                    <svg v-else-if="docFileType(item) === 'docx'" viewBox="0 0 24 24" class="fa-attachment-logo docx" aria-hidden="true">
                      <rect x="2" y="3" width="20" height="18" rx="3" fill="currentColor"></rect>
                      <text x="12" y="13" text-anchor="middle" dominant-baseline="middle" font-size="10" font-family="Arial, sans-serif" font-weight="700" fill="#fff">W</text>
                    </svg>
                    <svg v-else-if="docFileType(item) === 'txt'" viewBox="0 0 24 24" class="fa-attachment-logo txt" aria-hidden="true">
                      <rect x="2" y="3" width="20" height="18" rx="3" fill="currentColor"></rect>
                      <text x="12" y="13" text-anchor="middle" dominant-baseline="middle" font-size="8.5" font-family="Arial, sans-serif" font-weight="700" fill="#fff">TXT</text>
                    </svg>
                    <svg v-else viewBox="0 0 24 24" class="fa-attachment-logo doc" aria-hidden="true">
                      <rect x="2" y="3" width="20" height="18" rx="3" fill="currentColor"></rect>
                      <text x="12" y="13" text-anchor="middle" dominant-baseline="middle" font-size="8.5" font-family="Arial, sans-serif" font-weight="700" fill="#fff">DOC</text>
                    </svg>
                  </span>
                </span>
                <span class="fa-attachment-name">{{ item.name }}</span>
                <span class="fa-attachment-right" aria-hidden="true">
                  <span v-if="item.status === 'queued'" class="fa-badge pending pulsing">PENDING</span>
                  <span
                    v-else-if="item.status === 'uploading'"
                    class="fa-badge upload"
                    :style="progressBadgeStyle('upload', item.progress || 0)"
                  >
                    UPLOAD {{ item.progress || 0 }}%
                  </span>
                  <span
                    v-else-if="item.status === 'processing'"
                    class="fa-badge processing pulsing"
                  >
                    LEARNING
                  </span>
                  <span v-else-if="item.status === 'failed'" class="fa-badge failed">FAILED</span>
                  <button
                    v-else
                    type="button"
                    class="fa-attachment-remove"
                    @click="removeUploadItem(item.id)"
                    :disabled="item.status === 'uploading' || deletingDocuments"
                    aria-label="Remove"
                  >
                    ×
                  </button>
                </span>
              </div>
            </div>
          </div>
          <!-- custom flex row (not b-input-group) so the side buttons stay fixed
               height and bottom-anchor as the textarea grows upward. -->
          <b-form @submit.prevent="sendMessage(false)" :class="['fa-input-row', inputRowTutorialClass]" autocomplete="off">
            <button
              v-if="userDocUploadsEnabled"
              type="button"
              class="fa-input-iconbtn fa-docs-chevron"
              :aria-label="sessionDocsExpanded ? 'Hide session documents' : 'Session documents'"
              title="Session documents"
              @click="sessionDocsExpanded = !sessionDocsExpanded"
            >
              <svg v-if="sessionDocsExpanded" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                <path d="m7.41 8.41 4.59 4.59 4.59-4.59L18 9.83l-6 6-6-6z"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                <path d="M7.41 14.59 12 10l4.59 4.59L18 13.17 12 7.17l-6 6z"/>
              </svg>
            </button>
            <!-- native <textarea> with our own autosize logic so rows=1 collapses
                 to a true single-line height. see autosizeInput() watcher below. -->
            <textarea
              ref="faInput"
              v-model="currentMessage"
              :class="['fa-input-textarea', 'form-control', textareaTutorialClass]"
              placeholder="Type your message here..."
              name="fleet-assistant-input"
              autocomplete="off"
              autocapitalize="none"
              spellcheck="false"
              aria-autocomplete="none"
              rows="1"
              @keydown="onInputKeydown"
              @input="autosizeInput"
              @focus="$emit('input-focus-change', true)"
              @blur="$emit('input-focus-change', false)"
            ></textarea>
            <button
              type="submit"
              class="fa-send-btn"
              :disabled="loading || uploadingDocuments || !currentMessage.trim()"
            >
              {{ loading ? 'Thinking...' : 'Send' }}
            </button>
          </b-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { send_chat_message, get_chat_session, clear_chat_session, get_tenant_config_settings, list_fa_documents_user, presign_fa_document_user, finalize_fa_document_user, delete_fa_document_user, bulk_delete_fa_documents_user, submit_chat_feedback } from '../networking/apis'
import { tbn_send_chat_message, tbn_get_chat_session, tbn_clear_chat_session, tbn_submit_chat_feedback } from '../networking/tbn_apis'
import {
  DotsAnimation,
  SpinnerAnimation,
  VanAnimation,
  CoachBusAnimation,
  SchoolBusAnimation,
  ShuttleAnimation,
  LimoAnimation,
  TaxiAnimation,
  animationComponents
} from './animations'
import { renderMarkdownToHtml } from '../utils/markdown_renderer'

export default {
  name: 'fleet_assistant',
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    DotsAnimation,
    SpinnerAnimation,
    VanAnimation,
    CoachBusAnimation,
    SchoolBusAnimation,
    ShuttleAnimation,
    LimoAnimation,
    TaxiAnimation
  },
  data() {
    return {
      messages: [],
      // true once loadPreviousMessages has returned — used to suppress
      // the welcome empty-state until we know whether session history exists.
      // without this, opening FA briefly flashes the welcome panel before
      // prior messages arrive from the server.
      sessionLoaded: false,
      currentMessage: '',
      loading: false,
      showThinking: false,
      thinkingTimer: null,
      pageContext: null,
      sessionId: null,
      showTimestamps: true,
      animationType: 'dots',
      modelName: 'gpt-5.2',
      userDocs: [],
      docsLoading: false,
      sessionDocsExpanded: false,
      uploadingDocuments: false,
      deletingDocuments: false,
      uploadItems: [],
      // true while the tutorial sequence is animating characters into the input.
      // toggles the halo class on the input + blocks submission side-effects.
      inputTutorialGlow: false,
      _uploadQueue: null,
      suggestedPrompts: [
        "How's my fleet doing today?",
        "What am I looking at on this page?",
        "Which vehicles need attention?",
        "Help me find a report",
        "What can you do?"
      ],
      // response ratings.
      // - messageRatings[index]: optimistic local state, set on click.
      //   drives the filled/active button glyph so the UI responds
      //   instantly.
      // - messageRatingsConfirmed[index]: true only after the POST
      //   resolves. gates the "Thanks ✓" indicator so that checkmark
      //   is an honest server-confirmed signal, not wishful thinking.
      messageRatings: {},
      messageRatingsConfirmed: {},
      ratingCommentIndex: null,
      ratingComment: '',
      ratingSubmitting: false,
      userDocDeleting: null,
      _userDocsPollTimer: null,
      _userDocsPollDelay: 2000,
      sessionEstimatedCostCents: 0,
      sessionEstimatedCostUsd: 0,
      sessionChatCostCents: 0,
      sessionChatCostUsd: 0,
      sessionDocsCostCents: 0,
      sessionDocsCostUsd: 0
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    // per-user highlight style for the tutorial, set in Manage Profile. the
    // input here mirrors the same pref the launcher reads. unknown/missing →
    // 'pulse' so existing users get the legacy halo behavior.
    highlightStyle() {
      const p = this.user && this.user.preferences
      const v = p && p.highlight_style
      return (v === 'chase' || v === 'ripple' || v === 'pulse' || v === 'magenta' || v === 'aurora') ? v : 'pulse'
    },
    // class applied to the input-row wrapper when tutorial typing is active
    // AND the selected style is one that renders via row-level pseudo-
    // elements (chase / ripple / aurora). pulse + magenta apply to the
    // textarea itself instead (see textareaTutorialClass).
    inputRowTutorialClass() {
      if (!this.inputTutorialGlow) return ''
      if (this.highlightStyle === 'chase')  return 'fa-input-row--tutorial-chase'
      if (this.highlightStyle === 'ripple') return 'fa-input-row--tutorial-ripple'
      if (this.highlightStyle === 'aurora') return 'fa-input-row--tutorial-aurora'
      return ''
    },
    // class applied to the textarea when the selected style is a
    // box-shadow-on-the-input-itself variant. pulse uses the legacy
    // rainbow-halo class; magenta uses its own single-color glow class.
    // row-based styles return '' here so they don't stack with box-shadow
    // halos on the textarea.
    textareaTutorialClass() {
      if (!this.inputTutorialGlow) return ''
      if (this.highlightStyle === 'pulse')   return 'fa-input-tutorial-glow'
      if (this.highlightStyle === 'magenta') return 'fa-input-tutorial-magenta'
      return ''
    },
    currentAnimationComponent() {
      return animationComponents[this.animationType] || 'DotsAnimation'
    },
    tenant_id() {
      return this.user.tenant
    },
    tenant_type() {
      return this.user.types && this.user.types[this.user.tenant] ? this.user.types[this.user.tenant] : 'CM'
    },
    enabled_feature_flags() {
      const featureMap = this.user && this.user.features && this.user.features[this.user.tenant]
        ? this.user.features[this.user.tenant]
        : {}
      return Object.keys(featureMap).filter(flag => featureMap[flag])
    },
    documentCount() {
      return (this.uploadItems || []).length
    },
    is_tbn() {
      return this.tenant_type === 'TBN'
    },
    isAdmin() {
      return this.user.role === 'admin'
    },
    userDocUploadsEnabled() {
      const features = this.user && this.user.features && this.user.features[this.user.tenant]
      return features ? features.user_document_uploads_enabled !== false : true
    },
    messageEstimatedCostDollars() {
      return (this.messages || []).reduce((sum, msg) => {
        if (!msg || msg.role !== 'assistant' || !msg.usage) return sum
        return sum + this.getUsageCostDollars(msg.usage)
      }, 0)
    },
    displaySessionCostDollars() {
      if (Number.isFinite(this.sessionEstimatedCostUsd) && this.sessionEstimatedCostUsd > 0) {
        return this.sessionEstimatedCostUsd
      }
      if (Number.isFinite(this.sessionEstimatedCostCents) && this.sessionEstimatedCostCents > 0) {
        return this.parseCostCents(this.sessionEstimatedCostCents, 0) / 100
      }
      return this.messageEstimatedCostDollars
    },
    sessionChatCostDollars() {
      if (Number.isFinite(this.sessionChatCostUsd) && this.sessionChatCostUsd > 0) {
        return this.sessionChatCostUsd
      }
      if (Number.isFinite(this.sessionChatCostCents) && this.sessionChatCostCents > 0) {
        return this.parseCostCents(this.sessionChatCostCents, 0) / 100
      }
      return this.messageEstimatedCostDollars
    },
    sessionDocsCostDollars() {
      if (Number.isFinite(this.sessionDocsCostUsd) && this.sessionDocsCostUsd > 0) {
        return this.sessionDocsCostUsd
      }
      if (Number.isFinite(this.sessionDocsCostCents) && this.sessionDocsCostCents > 0) {
        return this.parseCostCents(this.sessionDocsCostCents, 0) / 100
      }
      return Math.max(0, this.displaySessionCostDollars - this.sessionChatCostDollars)
    },
    lastAssistantCostDollars() {
      for (let idx = this.messages.length - 1; idx >= 0; idx--) {
        const msg = this.messages[idx]
        if (!msg || msg.role !== 'assistant' || !msg.usage) continue
        const value = this.getUsageCostDollars(msg.usage)
        if (value > 0) {
          return value
        }
      }
      return 0
    },
  },
  watch: {
    // re-run autosize whenever currentMessage is set programmatically (e.g. the
    // tutorial typing animation mutates currentMessage char-by-char; @input on
    // the textarea doesn't fire for v-model writes from JS).
    currentMessage() {
      this.$nextTick(() => this.autosizeInput())
    },
    '$route.fullPath'() {
      this.gatherPageContext()
    },
    tenant_id(newTenantId, oldTenantId) {
      // tenant switched - need fresh chat
      if (oldTenantId && newTenantId !== oldTenantId) {
        console.log('Tenant changed from', oldTenantId, 'to', newTenantId, '- reloading chat session')
        this.messages = []
        this.sessionEstimatedCostCents = 0
        this.sessionEstimatedCostUsd = 0
        this.sessionChatCostCents = 0
        this.sessionChatCostUsd = 0
        this.sessionDocsCostCents = 0
        this.sessionDocsCostUsd = 0
        this.loadPreviousMessages()
      }
    }
  },
  mounted() {
    // guard: restrict to admin, customer-admin, tbn-admin, manager, dispatch
    const role = this.user && this.user.role
    const allowedRoles = ['admin', 'customer-admin', 'tbn-admin', 'manager', 'dispatch']
    if (!allowedRoles.includes(role)) {
      this.$router.replace({ name: 'home' })
      return
    }
    const features = this.user && this.user.features && this.user.features[this.user.tenant]
    if (features && features.fleet_assistant_enabled === false) {
      this.$router.replace({ name: 'home' })
      return
    }
    this.gatherPageContext()
    this.loadPreviousMessages()
    this.loadTenantSettings()
    this.load_user_docs()
    this._onLinkClick = this.handleRenderedLinkClick.bind(this)
    if (this.$refs.messagesContainer) {
      this.$refs.messagesContainer.addEventListener('click', this._onLinkClick)
    }
    this.$root.$on('fa-settings-changed', this.loadTenantSettings)
    this.$root.$on('fa-docs-changed', this.load_user_docs)
    // initial autosize so the textarea lands at single-line height (the CSS
    // line-height + padding can produce an off-by-one if we let the browser's
    // default rows=1 sizing apply unadjusted).
    this.$nextTick(() => this.autosizeInput())
  },
  beforeDestroy() {
    this.stopUserDocsRefresh()
    if (this.$refs.messagesContainer && this._onLinkClick) {
      this.$refs.messagesContainer.removeEventListener('click', this._onLinkClick)
    }
    this.$root.$off('fa-settings-changed', this.loadTenantSettings)
    this.$root.$off('fa-docs-changed', this.load_user_docs)
  },
  methods: {
    async removeUploadItem(id) {
      const item = (this.uploadItems || []).find(x => x.id === id)
      if (!item) return
      if (item.status === 'uploading' || item.status === 'processing') return

      // If already uploaded, delete from the user's document library too
      if (item.status === 'uploaded' && item.doc_id) {
        try {
          await delete_fa_document_user(this.tenant_id, item.doc_id)
          await this.load_user_docs()
        } catch (e) {
          const msg = e && e.response && e.response.data && e.response.data.error ? e.response.data.error : 'Delete failed'
          this.$emit('showAlert', msg, 'danger', 5000)
          return
        }
      }

      this.uploadItems = (this.uploadItems || []).filter(x => x.id !== id)
    },
    async deleteAllDocuments() {
      const items = (this.uploadItems || []).slice()
      const docIds = items.filter(x => x.doc_id && (x.status === 'uploaded' || x.status === 'failed')).map(x => x.doc_id)
      this.deletingDocuments = true
      try {
        if (docIds.length) {
          await bulk_delete_fa_documents_user(this.tenant_id, docIds)
        }
        this.uploadItems = []
        await this.load_user_docs()
      } catch (e) {
        var msg = e && e.response && e.response.data && e.response.data.error ? e.response.data.error : 'Delete failed'
        this.$emit('showAlert', msg, 'danger', 5000)
      } finally {
        this.deletingDocuments = false
      }
    },
    handleRenderedLinkClick(event) {
      var anchor = event.target.closest('a[data-spa-nav], a[data-help-article]')
      if (!anchor) return
      event.preventDefault()
      var href = anchor.getAttribute('href')
      if (href && this.$router) {
        this.$router.push(href).catch(function () {})
      }
    },
    sendSuggestedPrompt(prompt) {
      this.currentMessage = prompt
      this.$nextTick(() => this.sendMessage(false))
    },
    rateResponse(index, rating) {
      const current = this.messageRatings[index]
      if (current === rating) return // no-op, same rating
      this.$set(this.messageRatings, index, rating)
      // clear confirmed flag — re-rating or first-rating is unconfirmed
      // until the POST below resolves.
      this.$set(this.messageRatingsConfirmed, index, false)
      // persist on the message object so it survives messages array rebuilds
      if (this.messages[index]) this.$set(this.messages[index], 'user_rating', rating)
      // always submit the rating immediately so it's saved even if the
      // user never clicks submit on the optional comment. backend upserts
      // on the {tenant, user, session, turn_index} key, so a later
      // submitRatingComment call updates the same row in place.
      //
      // race-guard: the Submit button on the optional-comment textarea is
      // gated on :disabled="ratingSubmitting". _submitRating flips that
      // flag at entry, so the user cannot race the comment-POST ahead of
      // this initial POST even if they type at superhuman speed. do not
      // remove the :disabled binding without replacing the guard.
      this._submitRating(index, rating, '')
      if (rating === 'negative' || rating === 'concern') {
        this.ratingCommentIndex = index
        this.ratingComment = ''
        this.$nextTick(() => {
          const el = this.$refs.ratingCommentInput
          if (el) (Array.isArray(el) ? el[0] : el).$el ? (Array.isArray(el) ? el[0] : el).$el.focus() : (Array.isArray(el) ? el[0] : el).focus()
        })
      }
    },
    submitRatingComment(index) {
      // rating was already saved on flag/thumbs-down click; only re-submit
      // if the user actually typed a comment (adds to the existing row).
      const comment = (this.ratingComment || '').trim()
      if (comment) {
        const rating = this.messageRatings[index]
        this._submitRating(index, rating, comment)
      }
      this.ratingCommentIndex = null
      this.ratingComment = ''
    },
    skipRatingComment(index) {
      // rating was already saved on flag/thumbs-down click; nothing to
      // persist here — just close the optional-comment textarea.
      this.ratingCommentIndex = null
      this.ratingComment = ''
    },
    async _submitRating(messageIndex, rating, comment) {
      const message = this.messages[messageIndex]
      if (!message || message.role !== 'assistant') return
      // find turn index (count assistant messages before this one)
      let turnIndex = 0
      for (let i = 0; i < messageIndex; i++) {
        if (this.messages[i].role === 'assistant') turnIndex++
      }
      // find preceding user message
      let userMessage = ''
      for (let i = messageIndex - 1; i >= 0; i--) {
        if (this.messages[i].role === 'user') { userMessage = this.messages[i].content; break }
      }
      const payload = {
        rating,
        turn_index: turnIndex,
        session_id: this.sessionId || '',
        user_message: (userMessage || '').substring(0, 500),
        assistant_response: (message.content || '').substring(0, 500),
        model: message.usage ? message.usage.model || '' : '',
        tokens: message.usage ? (message.usage.input_tokens || 0) + (message.usage.output_tokens || 0) : null,
        cost: message.usage ? message.usage.estimated_cost_usd || null : null,
        page_context: this.pageContext ? JSON.stringify(this.pageContext).substring(0, 300) : '',
        route_name: this.$route && this.$route.name ? this.$route.name : '',
        comment: (comment || '').trim(),
      }
      this.ratingSubmitting = true
      try {
        const fn = this.is_tbn ? tbn_submit_chat_feedback : submit_chat_feedback
        await fn(this.tenant_id, payload)
        // mark confirmed only after the server accepted it. the "Thanks ✓"
        // indicator in the template gates on this, not on messageRatings,
        // so a silent network failure doesn't show a false positive.
        this.$set(this.messageRatingsConfirmed, messageIndex, true)
      } catch (e) {
        console.warn('Failed to submit FA rating:', e)
      } finally {
        this.ratingSubmitting = false
      }
    },
    openDocPicker() {
      if (this.$refs.faDocPicker) this.$refs.faDocPicker.click()
    },
    reloadSettings() {
      this.loadTenantSettings()
      this.load_user_docs()
    },
    focusInput() {
      this.$nextTick(() => {
        const ref = this.$refs.faInput
        if (!ref) return
        const el = ref.$el || ref
        if (el && typeof el.focus === 'function') el.focus()
      })
    },
    // Enter sends; Shift+Enter inserts a newline so the user can compose multi-line
    // questions. after a newline we re-run autosize so the textarea expands.
    onInputKeydown(e) {
      if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
        e.preventDefault()
        this.sendMessage(false)
      }
    },
    // resize the textarea to fit its content, capped at MAX_ROWS. reset height
    // to 'auto' first so shrinking works — scrollHeight otherwise sticks at
    // its previous tall value and the textarea never collapses back.
    autosizeInput() {
      const ta = this.$refs.faInput
      if (!ta) return
      const MAX_ROWS = 6
      ta.style.height = 'auto'
      const cs = window.getComputedStyle(ta)
      const lineHeight = parseFloat(cs.lineHeight) || 22
      const padTop = parseFloat(cs.paddingTop) || 0
      const padBottom = parseFloat(cs.paddingBottom) || 0
      const borderTop = parseFloat(cs.borderTopWidth) || 0
      const borderBottom = parseFloat(cs.borderBottomWidth) || 0
      const chromePx = padTop + padBottom + borderTop + borderBottom
      const maxPx = lineHeight * MAX_ROWS + chromePx
      const desired = Math.min(ta.scrollHeight, maxPx)
      ta.style.height = desired + 'px'
      ta.style.overflowY = ta.scrollHeight > maxPx ? 'auto' : 'hidden'
    },
    // pre-fills the FA input with a query, but does NOT auto-send. lets the
    // user review or edit before pressing enter. called by the launcher when
    // opened via $root.$emit('open-fleet-assistant', { initialQuery }).
    setInitialMessage(text) {
      if (typeof text === 'string' && text.trim()) {
        this.currentMessage = text
        this.focusInput()
      }
    },
    // tutorial-mode: highlight the input and animate the text in char-by-char.
    // used by the "Ask Fleet Assistant" pill in the help center no-results pane.
    // does NOT auto-send — user still reviews and presses enter.
    // opts.speed: 'slow' | 'full' (default) | 'fast'  — matches the user's
    // fa_tutorial_mode preference set in Manage Profile.
    async typeMessage(text, opts = {}) {
      if (typeof text !== 'string' || !text.trim()) return
      const wait = (ms) => new Promise((r) => setTimeout(r, ms))
      const speed = opts.speed
      // base delay per character + jitter + final hold, tuned per speed preset
      let baseDelay, jitter, holdMs
      if (speed === 'slow') { baseDelay = 70; jitter = 35; holdMs = 1100 }
      else if (speed === 'fast') { baseDelay = 12; jitter = 10; holdMs = 260 }
      else { baseDelay = 38; jitter = 22; holdMs = 650 }  // 'full' / default
      this.inputTutorialGlow = true
      this.currentMessage = ''
      await this.$nextTick()
      this.focusInput()
      const chars = Array.from(text)
      for (const ch of chars) {
        this.currentMessage += ch
        await wait(baseDelay + Math.floor(Math.random() * jitter))
      }
      // brief hold so the user registers the full message before the halo fades
      await wait(holdMs)
      this.inputTutorialGlow = false
    },
    onDocPickerChange(e) {
      const files = e && e.target && e.target.files ? Array.from(e.target.files) : []
      // reset so selecting same file again triggers change
      if (e && e.target) e.target.value = null
      if (!files.length) return
      this.enqueueUploads(files)
    },
    enqueueUploads(files) {
      if (!files || !files.length) return
      const nowSec = Math.floor(Date.now() / 1000)
      const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25 MB
      const MAX_DOCS = 25
      // count server-side docs + in-flight uploads (deduplicating by doc_id)
      const serverDocIds = new Set((this.userDocs || []).map(d => d.doc_id).filter(Boolean))
      const inFlightCount = (this.uploadItems || []).filter(x =>
        x.status !== 'failed' && !(x.doc_id && serverDocIds.has(x.doc_id))
      ).length
      const currentCount = serverDocIds.size + inFlightCount

      const newItems = []
      for (let idx = 0; idx < files.length; idx++) {
        const f = files[idx]
        const item = {
          id: `${Date.now()}-${Math.random().toString(16).slice(2)}-${idx}`,
          name: f.name,
          file: f,
          status: 'queued',
          error: null,
          progress: 0,
          uploaded_at: nowSec,
        }
        if (f.size > MAX_FILE_SIZE) {
          item.status = 'failed'
          item.error = `${f.name} is ${(f.size / (1024 * 1024)).toFixed(1)} MB — maximum is 25 MB`
          item.file = null
        } else if (currentCount + newItems.filter(x => x.status === 'queued').length >= MAX_DOCS) {
          item.status = 'failed'
          item.error = `Document limit reached (${MAX_DOCS}). Delete a document to add another.`
          item.file = null
        }
        newItems.push(item)
      }
      this.uploadItems = [...(this.uploadItems || []), ...newItems]

      if (!this._uploadQueue) this._uploadQueue = Promise.resolve()
      this._uploadQueue = this._uploadQueue
        .then(() => this._processUploadQueue())
        .catch(() => this._processUploadQueue())
    },
    async _processUploadQueue() {
      // prevent re-entrancy
      if (this.uploadingDocuments) return

      const queued = (this.uploadItems || []).filter(x => x.status === 'queued')
      if (!queued.length) return

      this.uploadingDocuments = true
      const CONCURRENCY = 3
      const finalizePromises = []
      let index = 0

      // each worker pulls the next queued item, uploads it to S3, fires off
      // finalize in the background, then immediately grabs the next item.
      const worker = async () => {
        while (index < queued.length) {
          const item = queued[index++]
          await this._uploadItem(item, finalizePromises)
        }
      }

      try {
        // spawn up to CONCURRENCY workers draining the queue in parallel
        await Promise.all(
          Array.from({ length: Math.min(CONCURRENCY, queued.length) }, () => worker())
        )
        // all S3 uploads done — wait for trailing finalize calls to settle
        await Promise.allSettled(finalizePromises)
        await this.load_user_docs()
      } finally {
        this.uploadingDocuments = false
      }
    },
    // handles presign + S3 upload, then kicks off finalize without blocking
    async _uploadItem(item, finalizePromises) {
      this.uploadItems = (this.uploadItems || []).map(x => x.id === item.id ? { ...x, status: 'uploading', error: null, progress: 0 } : x)
      try {
        const file = item.file
        const filename = file && file.name ? file.name : 'document'
        const contentType = file && file.type ? file.type : 'application/octet-stream'
        const presignResp = await presign_fa_document_user(this.tenant_id, {
          filename,
          content_type: contentType,
          file_size: file && file.size ? file.size : 0,
          display_name: filename,
        })
        const presignData = presignResp && presignResp.value && presignResp.value.data ? (presignResp.value.data.data || presignResp.value.data) : {}
        const upload = presignData.upload || {}
        const uploadForm = new FormData()
        Object.entries(upload.fields || {}).forEach(([k, v]) => uploadForm.append(k, v))
        uploadForm.append('file', file)
        await this.uploadToS3WithProgress(upload.url, uploadForm, (pct) => {
          this.uploadItems = (this.uploadItems || []).map(x => x.id === item.id ? { ...x, progress: pct } : x)
        })
        // S3 upload done — mark processing and set doc_id so the merge logic
        // in load_user_docs can match this item against the server version.
        this.uploadItems = (this.uploadItems || []).map(x => x.id === item.id ? { ...x, status: 'processing', error: null, progress: 0, doc_id: presignData.doc_id } : x)
        finalizePromises.push(this._finalizeItem(item.id, presignData, filename, contentType))
        // worker returns immediately to pick up the next queued item
      } catch (err) {
        const msg = err && err.response && err.response.data && err.response.data.error ? err.response.data.error : 'Upload failed'
        console.warn('[FA Docs] Upload error', err)
        this.uploadItems = (this.uploadItems || []).map(x => x.id === item.id ? { ...x, status: 'failed', error: msg, progress: 0 } : x)
      }
    },
    async _finalizeItem(itemId, presignData, filename, contentType) {
      try {
        const finalizeResp = await finalize_fa_document_user(this.tenant_id, {
          doc_id: presignData.doc_id,
          filename,
          content_type: contentType,
          display_name: presignData.display_name || filename,
        })
        const payload = finalizeResp && finalizeResp.value && finalizeResp.value.data ? (finalizeResp.value.data.data || finalizeResp.value.data) : {}
        const docId = payload && payload.doc_id ? payload.doc_id : null
        this.applySessionCostFromPayload(payload)
        this.uploadItems = (this.uploadItems || []).map(x => x.id === itemId ? { ...x, status: 'uploaded', error: null, doc_id: docId, file: null, progress: 100 } : x)
      } catch (err) {
        const msg = err && err.response && err.response.data && err.response.data.error ? err.response.data.error : 'Processing failed'
        this.uploadItems = (this.uploadItems || []).map(x => x.id === itemId ? { ...x, status: 'failed', error: msg, progress: 0 } : x)
      }
    },
    gatherPageContext() {
      var navbar = document.getElementById('navbar')
      // launcher root is `.fa-launcher-root` (the floating widget wrapper);
      // the earlier `.fa-widget-root` selector matched nothing, so the
      // launcher chrome (header, resize handles, etc.) leaked into the
      // page-context scrape.
      var faLauncher = document.querySelector('.fa-launcher-root')
      // global service-alert banner sits above the router-view and is
      // chrome from FA's point of view; skip it so its text doesn't get
      // mistaken for the current page's content (FI-1612).
      var serviceBanner = document.querySelector('.sab-banner')
      var isChrome = function (node) {
        if (!node) return false
        if (navbar && navbar.contains(node)) return true
        if (faLauncher && faLauncher.contains(node)) return true
        if (serviceBanner && serviceBanner.contains(node)) return true
        return false
      }
      var isInsideAssistant = function (node) {
        return this.$el && node && this.$el.contains(node)
      }.bind(this)
      var compactText = function (text) { return (text || '').replace(/\s+/g, ' ').trim() }

      var context = {
        page_title: document.title,
        route_name: this.$route.name,
        route_path: this.$route.path,
        route_query: this.$route.query || {},
        route_params: this.$route.params || {},
        url: window.location.href,
        timestamp: new Date().toISOString()
      }

      var headingTexts = Array.from(document.querySelectorAll('h1, h2, h3'))
        .filter(function (h) { return !isInsideAssistant(h) && !isChrome(h) })
        .map(function (h) { return compactText(h.textContent) })
        .filter(Boolean)
        .slice(0, 5)

      var textNodes = Array.from(document.querySelectorAll('h1, h2, h3, h4, p, td, th, label, button, a, span, div.col, div.text-center'))
      var seen = {}
      var visibleText = []
      var visibleTextLength = 0
      for (var idx = 0; idx < textNodes.length; idx++) {
        var node = textNodes[idx]
        if (isInsideAssistant(node) || isChrome(node)) continue
        var value = compactText(node.innerText || node.textContent)
        if (!value || value.length < 3) continue
        if (seen[value]) continue
        seen[value] = true
        visibleText.push(value)
        visibleTextLength += value.length + 1
        if (visibleTextLength >= 1500) break
      }

      this.pageContext = {
        ...context,
        headings: headingTexts,
        visible_text: visibleText.join(' ').slice(0, 1500)
      }
    },
    // mirror the filter from service_alert_banner.vue: unread service_alert
    // notifications with banner=true whose expiry (if set) is still in the
    // future. these are product-wide operational alerts that FA should be
    // aware of even when the user doesn't mention them, since they often
    // explain otherwise-confusing fleet behavior (delayed data, outages).
    gatherActiveBanners() {
      const store = this.$store && this.$store.state && this.$store.state.notifications
      const notifications = store && Array.isArray(store.notifications) ? store.notifications : []
      if (!notifications.length) return []
      const nowMs = Date.now()
      const isFuture = function (iso) {
        if (!iso) return true
        const t = Date.parse(iso)
        if (Number.isNaN(t)) return true
        return t > nowMs
      }
      return notifications
        .filter(function (n) {
          return n
            && n.type === 'service_alert'
            && n.banner === true
            && !n.read_at
            && isFuture(n.banner_expires_at)
        })
        .slice(0, 3)
        .map(function (n) {
          return {
            title: String(n.title || '').slice(0, 160),
            body_preview: String(n.body_preview || '').slice(0, 280),
            type: n.type,
            created_at: n.created_at || null,
          }
        })
    },
    async sendMessage(continueResponse = false) {
      // empty msg ok if we're just continuing
      if (!continueResponse && (!this.currentMessage.trim() || this.loading || this.uploadingDocuments)) {
        return
      }

      // refresh context just before request so route/page changes are reflected
      this.gatherPageContext()

      const userMessage = continueResponse ? 'continue' : this.currentMessage.trim()
      if (!continueResponse) {
        this.currentMessage = ''
      }
      
      // only show user msg bubble for actual messages, not "continue"
      if (!continueResponse) {
        this.messages.push({
          role: 'user',
          content: userMessage,
          timestamp: new Date().toISOString()
        })
      }
      
      // clear old truncation marker when continuing
      if (continueResponse && this.messages.length > 0 && this.messages[this.messages.length - 1].role === 'assistant') {
        this.messages[this.messages.length - 1].is_truncated = false
      }

      this.$nextTick(() => {
        this.scrollToBottom()
      })

      this.loading = true
      this.thinkingTimer = setTimeout(() => {
        this.showThinking = true
        this.$nextTick(() => { this.scrollToBottom() })
      }, 1000)

      try {
        const userDocIds = (this.uploadItems || [])
          .filter(item => item && item.doc_id && item.status === 'uploaded')
          .map(item => item.doc_id)
        const fleetContext = {
          page_context: this.pageContext,
          user_role: this.user.role,
          tenant_name: this.user.tenant_name || this.tenant_id,
          current_page: this.$route.name,
          tenant_type: this.tenant_type,
          enabled_features: this.enabled_feature_flags,
          active_banners: this.gatherActiveBanners()
        }

        const payload = {
          message: userMessage,
          fleet_context: fleetContext,
          tenant: this.tenant_id,
          continue_response: continueResponse,
          include_company_docs: true,
          include_user_docs: userDocIds.length > 0,
          user_doc_ids: userDocIds,
          company_doc_ids: []
        }

        console.log('FleetAssistant: Sending message', { 
          userMessage: userMessage.substring(0, 50), 
          continueResponse, 
          messageLength: userMessage.length,
          is_tbn: this.is_tbn
        })

        // use TBN API for TBN tenants, regular API for others
        const chatFunction = this.is_tbn ? tbn_send_chat_message : send_chat_message
        const { value: { data: responseData = {} } } = await chatFunction(this.tenant_id, payload)
        
        const data = responseData.data || responseData // unwrap the ApiSuccessResponse
        this.applySessionCostFromPayload(data)

        const reply = data.reply || 'Sorry, I could not generate a response. Please try again.'
        const isTruncated = data.is_truncated || false
        const continuationUsage = this.extractLatestAssistantUsage(data.previous_messages)
        
        // store session id for admin display
        if (data.session_id) {
          this.sessionId = data.session_id
        }
        
        // append to existing msg if continuing, otherwise add new bubble
        if (continueResponse && this.messages.length > 0 && this.messages[this.messages.length - 1].role === 'assistant') {
          this.messages[this.messages.length - 1].content = this.messages[this.messages.length - 1].content + "\n\n" + reply
          this.messages[this.messages.length - 1].is_truncated = isTruncated
          if (continuationUsage) {
            this.messages[this.messages.length - 1].usage = this.mergeUsage(
              this.messages[this.messages.length - 1].usage,
              continuationUsage
            )
          }
        } else {
          // server returns last 10 turns - just use those
          if (data.previous_messages && Array.isArray(data.previous_messages) && data.previous_messages.length > 0) {
            // build lookups of existing client-side timestamps and ratings by content+role
            const existingTimestamps = {}
            const existingRatings = {}
            for (const m of this.messages) {
              const k = m.role + ':' + (m.content || '').substring(0, 80)
              if (m.timestamp) existingTimestamps[k] = m.timestamp
              if (m.user_rating) existingRatings[k] = m.user_rating
            }
            const now = new Date().toISOString()
            this.messages = data.previous_messages.map((msg, idx) => {
              // preserve client-side timestamp and rating if we have them
              const key = msg.role + ':' + (msg.content || '').substring(0, 80)
              const isLatestAssistant = msg.role === 'assistant' && idx === data.previous_messages.length - 1
              const timestamp = msg.timestamp || existingTimestamps[key] || (isLatestAssistant ? now : '')
              const user_rating = msg.user_rating || existingRatings[key] || ''
              if (isLatestAssistant) {
                return { ...msg, is_truncated: isTruncated, timestamp, user_rating }
              }
              return { ...msg, timestamp, user_rating }
            })
            // restore messageRatings from message objects. persisted
            // ratings are server-confirmed, so rehydrate the confirmed
            // map too — see loadPreviousMessages for the same pattern.
            this.messageRatings = {}
            this.messageRatingsConfirmed = {}
            for (let i = 0; i < this.messages.length; i++) {
              if (this.messages[i].user_rating) {
                this.$set(this.messageRatings, i, this.messages[i].user_rating)
                this.$set(this.messageRatingsConfirmed, i, true)
              }
            }
            console.log('Updated messages from server:', this.messages.length)
          } else {
            // fallback - shouldnt really happen
            console.log('Chat response received:', { session_id: data.session_id, reply_length: reply.length, is_truncated: isTruncated })
            this.messages.push({
              role: 'assistant',
              content: reply,
              is_truncated: isTruncated,
              timestamp: new Date().toISOString()
            })
          }
        }
        
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      } catch (error) {
        console.error('Error sending message:', error)
        const isOverLimit = error.response && error.response.status === 429 && error.response.data && error.response.data.over_limit
        const msg = error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : 'Error sending message. Please try again or contact support if the issue persists.'

        if (isOverLimit) {
          // over-limit is final — no polling recovers it.
          this.$emit('showAlert', msg, 'warning', 10000)
          this.messages.push({
            role: 'assistant',
            over_limit: true,
            content: 'Your organization has reached its monthly AI usage limit. I can\'t respond to new questions until your usage resets or your budget increases. Previous conversations and help articles remain accessible. AI usage resets monthly.',
            timestamp: new Date().toISOString()
          })
        } else {
          // client timed out (api gw 29s) or the connection was aborted.
          // backend may have completed and written the turn to the session
          // anyway, so poll briefly before giving up and showing the error.
          const recovered = await this.pollForMissedResponse()
          if (!recovered) {
            this.$emit('showAlert', msg, 'danger', 5000)
            this.messages.push({
              role: 'assistant',
              content: 'Sorry, I encountered an error. Please try again or rephrase your question.',
              timestamp: new Date().toISOString()
            })
          }
        }
      } finally {
        clearTimeout(this.thinkingTimer)
        this.showThinking = false
        this.loading = false
        this.uploadingDocuments = false
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },

    // (legacy) onFilesSelected removed — uploads now start from file dialog
    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight
      }
    },
    continueResponse() {
      this.sendMessage(true) // pick up where we left off
    },
    goToUsageRequest() {
      // modal lives globally in App.vue — open it in place so the user stays on whatever
      // page they were on when the limit callout showed up.
      this.$root.$emit('open-ai-usage-request-modal')
    },
    renderMarkdown(text) {
      return renderMarkdownToHtml(text, {
        stripAssistantClosings: true,
        addLastElementClass: true,
      })
    },
    async pollForMissedResponse() {
      // when the chat POST fails (504 / network abort) but the backend
      // may have finished and persisted the turn to the session, poll
      // briefly to recover the response instead of showing a false error.
      // returns true if a new assistant turn was found and merged.
      const sessionFunction = this.is_tbn ? tbn_get_chat_session : get_chat_session
      const startCount = this.messages.length
      const deadline = Date.now() + 20000
      const intervalMs = 3000
      while (Date.now() < deadline) {
        await new Promise(resolve => setTimeout(resolve, intervalMs))
        try {
          const { value: { data: responseData = {} } } = await sessionFunction(this.tenant_id)
          const data = responseData.data || responseData
          if (data.messages && Array.isArray(data.messages) && data.messages.length >= startCount + 1) {
            const lastMsg = data.messages[data.messages.length - 1]
            if (lastMsg && lastMsg.role === 'assistant') {
              this.applySessionCostFromPayload(data)
              if (data.session_id) this.sessionId = data.session_id
              this.messages = data.messages.map(msg => ({
                ...msg,
                timestamp: msg.timestamp || new Date().toISOString(),
                user_rating: msg.user_rating || '',
              }))
              this.messageRatings = {}
              this.messageRatingsConfirmed = {}
              for (let i = 0; i < this.messages.length; i++) {
                if (this.messages[i].user_rating) {
                  this.$set(this.messageRatings, i, this.messages[i].user_rating)
                  this.$set(this.messageRatingsConfirmed, i, true)
                }
              }
              this.$nextTick(() => this.scrollToBottom())
              console.log('pollForMissedResponse: recovered response after client timeout')
              return true
            }
          }
        } catch (e) {
          console.warn('pollForMissedResponse: session fetch failed, will retry', e)
        }
      }
      return false
    },
    async loadPreviousMessages() {
      // reset on entry so tenant-switch reloads also suppress the welcome
      // flash until the new session has been fetched.
      this.sessionLoaded = false
      try {
        this.loading = true
        console.log('Loading previous messages from server-side session', { is_tbn: this.is_tbn })

        // use TBN API for TBN tenants, regular API for others
        const sessionFunction = this.is_tbn ? tbn_get_chat_session : get_chat_session
        const { value: { data: responseData = {} } } = await sessionFunction(this.tenant_id)
        const data = responseData.data || responseData
        this.applySessionCostFromPayload(data)

        // store session id for admin display
        if (data.session_id) {
          this.sessionId = data.session_id
        }

        if (data.messages && Array.isArray(data.messages) && data.messages.length > 0) {
          // ensure messages have timestamps and preserve any existing ratings
          this.messages = data.messages.map(msg => ({
            ...msg,
            timestamp: msg.timestamp || null,
            user_rating: msg.user_rating || '',
          }))
          // restore messageRatings from loaded messages. a persisted
          // rating is, by definition, confirmed — rehydrate the
          // confirmed map so reloading the session shows "Thanks ✓".
          this.messageRatings = {}
          this.messageRatingsConfirmed = {}
          for (let i = 0; i < this.messages.length; i++) {
            if (this.messages[i].user_rating) {
              this.$set(this.messageRatings, i, this.messages[i].user_rating)
              this.$set(this.messageRatingsConfirmed, i, true)
            }
          }
          console.log('Loaded previous messages:', this.messages.length)
          this.$nextTick(() => {
            this.scrollToBottom()
          })
        }
      } catch (error) {
        console.error('Error loading previous messages:', error)
        // no session yet = empty chat, thats fine
      } finally {
        this.loading = false
        this.sessionLoaded = true
      }
    },
    async loadTenantSettings() {
      try {
        const { value: { data = {} } } = await get_tenant_config_settings(this.tenant_id)
        const settings = data.results || {}
        if (settings.fleet_assistant_animation) {
          this.animationType = settings.fleet_assistant_animation
        }
        if (settings.openai_model) {
          this.modelName = settings.openai_model
        }
      } catch (error) {
        console.error('Error loading tenant settings:', error)
        // use defaults
      }
    },
    async load_user_docs() {
      this.docsLoading = true
      try {
        const { value: { data = {} } } = await list_fa_documents_user(this.tenant_id)
        const results = data.data || data
        this.userDocs = results.documents || []

        // merge server docs into uploadItems (preserve any in-progress uploads)
        const serverDocs = this.userDocs.map(doc => ({
          id: doc.doc_id,
          name: doc.display_name || doc.doc_id,
          file: null,
          status: doc.status || 'uploaded',
          error: null,
          doc_id: doc.doc_id,
          doc_type: doc.doc_type,
          content_type: doc.content_type,
          uploaded_at: doc.uploaded_at || 0,
        }))
        // build server status lookup so we can detect when the server has
        // moved past 'processing'-- in that case the server version wins.
        const serverStatusMap = new Map(serverDocs.filter(d => d.doc_id).map(d => [d.doc_id, d.status]))
        const inProgress = (this.uploadItems || []).filter(x => {
          if (x.status === 'queued' || x.status === 'uploading') return true
          if (x.status === 'processing') {
            if (!x.doc_id) return true // presign hasn't returned yet
            const srvStatus = serverStatusMap.get(x.doc_id)
            // Keep local version only if server doesn't know about it yet
            // or server also says 'processing'; otherwise let server win.
            return !srvStatus || srvStatus === 'processing'
          }
          return false
        })
        // combine: in-progress first, then server docs (excluding duplicates)
        const inProgressIds = new Set(inProgress.map(x => x.doc_id).filter(Boolean))
        const merged = [...inProgress, ...serverDocs.filter(d => !inProgressIds.has(d.doc_id))]
        this.uploadItems = this.sortByUploadedAt(merged)
        this.scheduleUserDocsRefresh()
      } catch (e) {
        console.warn('Failed to load user documents:', e)
        this.scheduleUserDocsRefresh()
      } finally {
        this.docsLoading = false
      }
    },
    async delete_user_doc(docId) {
      if (!confirm('Delete this document?')) return
      this.userDocDeleting = docId
      try {
        await delete_fa_document_user(this.tenant_id, docId)
        await this.load_user_docs()
        this.$emit('showAlert', 'Document deleted', 'success', 3000)
      } catch (e) {
        this.$emit('showAlert', e.response && e.response.data && e.response.data.error ? e.response.data.error : 'Delete failed', 'danger', 5000)
      } finally {
        this.userDocDeleting = null
      }
    },
    uploadToS3WithProgress(url, formData, onProgress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('POST', url, true)
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve()
          } else {
            // S3 returns XML error bodies — try to extract a useful message
            let detail = `S3 upload failed (HTTP ${xhr.status})`
            try {
              const xml = xhr.responseText || ''
              const codeMatch = xml.match(/<Code>([^<]+)<\/Code>/)
              const msgMatch = xml.match(/<Message>([^<]+)<\/Message>/)
              if (codeMatch || msgMatch) {
                detail = [codeMatch && codeMatch[1], msgMatch && msgMatch[1]].filter(Boolean).join(': ')
              }
            } catch (_) { /* ignore parse errors */ }
            reject(new Error(detail))
          }
        }
        xhr.onerror = () => reject(new Error('S3 upload failed (network error)'))
        xhr.upload.onprogress = (event) => {
          if (!event.lengthComputable || typeof onProgress !== 'function') return
          const pct = Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)))
          onProgress(pct)
        }
        xhr.send(formData)
      })
    },
    progressBadgeStyle(kind, pct) {
      const clamped = Math.max(0, Math.min(100, Math.round(pct || 0)))
      const color = kind === 'processing' ? '#1e7eeb' : '#28a745'
      const bg = '#e9ecef'
      return {
        '--progress': `${clamped}%`,
        '--badge-color': color,
        '--badge-bg': bg
      }
    },
    sortByUploadedAt(items) {
      return (items || []).slice().sort((a, b) => {
        const aTs = Number(a && a.uploaded_at) || 0
        const bTs = Number(b && b.uploaded_at) || 0
        return bTs - aTs
      })
    },
    scheduleUserDocsRefresh() {
      const serverProcessing = (this.userDocs || []).some(d => (d.status || 'uploaded') === 'processing')
      const localProcessing = (this.uploadItems || []).some(x => x.status === 'processing')
      if (!serverProcessing && !localProcessing) {
        this.stopUserDocsRefresh()
        return
      }
      if (this._userDocsPollTimer) return
      const delay = this._userDocsPollDelay || 2000
      this._userDocsPollTimer = setTimeout(async () => {
        this._userDocsPollTimer = null
        if (this.docsLoading) {
          this.scheduleUserDocsRefresh()
          return
        }
        await this.load_user_docs()
        this._userDocsPollDelay = Math.min(Math.round((this._userDocsPollDelay || 2000) * 1.5), 15000)
      }, delay)
    },
    stopUserDocsRefresh() {
      if (this._userDocsPollTimer) {
        clearTimeout(this._userDocsPollTimer)
        this._userDocsPollTimer = null
      }
      this._userDocsPollDelay = 2000
    },
    docFileType(item) {
      const name = (item && (item.name || (item.file && item.file.name))) || ''
      const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : ''
      if (ext === 'pdf') return 'pdf'
      if (ext === 'docx' || ext === 'doc') return 'docx'
      if (ext === 'txt') return 'txt'
      const ct = (item && item.content_type) || ''
      if (ct.includes('pdf')) return 'pdf'
      if (ct.includes('word')) return 'docx'
      if (ct.includes('text')) return 'txt'
      return 'doc'
    },
    async clearSession() {
      if (!confirm('Clear conversation and start fresh? This cannot be undone.')) {
        return
      }
      
      try {
        this.loading = true
        const clearFunction = this.is_tbn ? tbn_clear_chat_session : clear_chat_session
        await clearFunction(this.tenant_id)
        this.messages = []
        // reset rating maps so stale index-keyed state from the old
        // session doesn't bleed into the first message of the new one.
        this.messageRatings = {}
        this.messageRatingsConfirmed = {}
        this.sessionId = null
        this.sessionEstimatedCostCents = 0
        this.sessionEstimatedCostUsd = 0
        this.sessionChatCostCents = 0
        this.sessionChatCostUsd = 0
        this.sessionDocsCostCents = 0
        this.sessionDocsCostUsd = 0
        console.log('Session cleared successfully')
      } catch (error) {
        console.error('Error clearing session:', error)
        this.$emit('showAlert', 'Error clearing session. Please try again.', 'danger', 3000)
      } finally {
        this.loading = false
      }
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return ''
      try {
        const date = new Date(timestamp)
        return date.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (e) {
        return ''
      }
    },
    formatTimestampFull(timestamp) {
      if (!timestamp) return ''
      try {
        const date = new Date(timestamp)
        return date.toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      } catch (e) {
        return ''
      }
    },
    formatCostDollars(dollars) {
      const normalized = this.parseCostDollars(dollars, 0)
      if (normalized <= 0) return '$0.0000'
      if (normalized < 0.0001) return '<$0.0001'
      const decimals = normalized < 1 ? 4 : 2
      return `$${normalized.toFixed(decimals)}`
    },
    parseCostCents(value, fallback = 0) {
      const parsed = Number(value)
      if (!Number.isFinite(parsed) || parsed < 0) return Math.max(0, Math.round(fallback || 0))
      return Math.round(parsed)
    },
    parseCostDollars(value, fallback = 0) {
      const parsed = Number(value)
      if (!Number.isFinite(parsed) || parsed < 0) {
        const fallbackNum = Number(fallback)
        return Number.isFinite(fallbackNum) ? fallbackNum : 0
      }
      return Math.max(0, parsed)
    },
    getUsageCostDollars(usage) {
      if (!usage || typeof usage !== 'object') return 0
      if (usage.estimated_cost_usd !== undefined && usage.estimated_cost_usd !== null) {
        return this.parseCostDollars(usage.estimated_cost_usd, 0)
      }
      return this.parseCostCents(usage.estimated_cost_cents, 0) / 100
    },
    applySessionCostFromPayload(payload) {
      if (!payload || typeof payload !== 'object') return
      if (payload.session_estimated_cost_cents !== undefined) {
        this.sessionEstimatedCostCents = this.parseCostCents(
          payload.session_estimated_cost_cents,
          this.sessionEstimatedCostCents
        )
      }
      if (payload.session_estimated_cost_usd !== undefined) {
        this.sessionEstimatedCostUsd = this.parseCostDollars(
          payload.session_estimated_cost_usd,
          this.sessionEstimatedCostUsd
        )
      }
      if (payload.session_chat_estimated_cost_cents !== undefined) {
        this.sessionChatCostCents = this.parseCostCents(
          payload.session_chat_estimated_cost_cents,
          this.sessionChatCostCents
        )
      }
      if (payload.session_chat_estimated_cost_usd !== undefined) {
        this.sessionChatCostUsd = this.parseCostDollars(
          payload.session_chat_estimated_cost_usd,
          this.sessionChatCostUsd
        )
      }
      if (payload.session_docs_estimated_cost_cents !== undefined) {
        this.sessionDocsCostCents = this.parseCostCents(
          payload.session_docs_estimated_cost_cents,
          this.sessionDocsCostCents
        )
      }
      if (payload.session_docs_estimated_cost_usd !== undefined) {
        this.sessionDocsCostUsd = this.parseCostDollars(
          payload.session_docs_estimated_cost_usd,
          this.sessionDocsCostUsd
        )
      }
    },
    normalizeUsage(rawUsage) {
      if (!rawUsage || typeof rawUsage !== 'object') return null
      return {
        input_tokens: this.parseCostCents(rawUsage.input_tokens, 0),
        output_tokens: this.parseCostCents(rawUsage.output_tokens, 0),
        estimated_cost_cents: this.parseCostCents(rawUsage.estimated_cost_cents, 0),
        estimated_cost_usd: this.parseCostDollars(
          rawUsage.estimated_cost_usd,
          this.parseCostCents(rawUsage.estimated_cost_cents, 0) / 100
        ),
        api_calls: this.parseCostCents(rawUsage.api_calls, 0),
        model: rawUsage.model || null
      }
    },
    mergeUsage(existingUsage, additionalUsage) {
      const current = this.normalizeUsage(existingUsage)
      const next = this.normalizeUsage(additionalUsage)
      if (!current) return next
      if (!next) return current
      return {
        input_tokens: current.input_tokens + next.input_tokens,
        output_tokens: current.output_tokens + next.output_tokens,
        estimated_cost_cents: current.estimated_cost_cents + next.estimated_cost_cents,
        estimated_cost_usd: current.estimated_cost_usd + next.estimated_cost_usd,
        api_calls: current.api_calls + next.api_calls,
        model: next.model || current.model || null
      }
    },
    extractLatestAssistantUsage(previousMessages) {
      if (!Array.isArray(previousMessages) || !previousMessages.length) return null
      for (let idx = previousMessages.length - 1; idx >= 0; idx--) {
        const msg = previousMessages[idx]
        if (msg && msg.role === 'assistant' && msg.usage) {
          return this.normalizeUsage(msg.usage)
        }
      }
      return null
    }
  }
}
</script>

<style scoped>
.fleet-assistant-root {
  margin-top: 20px;
}

.fleet-assistant-shell {
  max-width: 1200px;
  margin: 0 auto;
}

.fleet-assistant-chat-wrapper {
  border: 1px solid #ccc;
  border-radius: 8px;
  height: 600px;
  display: flex;
  flex-direction: column;
  background-color: white;
  overflow: hidden;
}

.fleet-assistant-root.is-embedded {
  margin-top: 0;
  height: 100%;
  overflow: hidden;
}

.fleet-assistant-root.is-embedded .fleet-assistant-shell {
  max-width: none;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.fleet-assistant-root.is-embedded .fleet-assistant-chat-wrapper {
  height: auto;
  flex: 1;
  min-height: 0;
  border: none;
  border-radius: 0;
}

/* chat scroll container + anchor wrapper. the flex column + `margin-top: auto`
   on the inner anchor pushes messages to the BOTTOM of the viewport when the
   conversation is short (latest message sits just above the input instead of
   floating at the top with a gap). when messages overflow the container, the
   auto margin collapses to 0 and normal scroll behavior takes over — with
   scrollToBottom() wiring already in place, the latest message stays visible. */
.fa-messages-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background-color: #f9f9f9;
  display: flex;
  flex-direction: column;
}
.fa-messages-anchor {
  margin-top: auto;
}

.fa-welcome-empty {
  text-align: center;
  color: #666;
  margin-top: 50px;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
}

.fa-welcome-title {
  font-size: 18px;
  margin: 0;
}

.fa-welcome-sub {
  font-size: 14px;
  margin-top: 10px;
  margin-bottom: 0;
}

.fa-welcome-list {
  text-align: left;
  display: inline-block;
  margin-top: 10px;
  padding-left: 20px;
}

.fa-welcome-try {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-top: 16px;
  margin-bottom: 8px;
}

.fa-suggested-prompts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

.fa-prompt-chip {
  font-size: 14px;
  padding: 10px 16px;
  border-radius: 999px;
  border: 1px solid rgba(0, 122, 199, 0.35);
  background: rgba(0, 122, 199, 0.08);
  color: #007ac7;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.fa-prompt-chip:hover:not(:disabled) {
  background: rgba(0, 122, 199, 0.14);
  border-color: rgba(0, 122, 199, 0.5);
}

.fa-prompt-chip:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.fa-session-docs {
  margin-bottom: 10px;
}

.fa-session-docs-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.fa-session-docs-spacer {
  flex: 1;
  min-width: 8px;
}

.fa-session-docs-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  width: 100%;
}

/* input row — flex layout so the side buttons bottom-anchor while the textarea
   grows upward. gap gives breathing room without BV's merged borders. */
.fa-input-row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  margin: 0;
}

/* shared baseline height for all three children; the textarea respects it via
   line-height + padding (NOT min-height — that confuses BV's auto-grow into
   thinking the content needs a second row). flex-end on the row keeps the
   buttons aligned at the bottom when the textarea grows multi-line. */
.fa-input-row .fa-input-textarea.form-control {
  flex: 1;
  padding: 9px 12px;
  line-height: 22px;
  border-radius: 8px;
  resize: none;
  font-size: 16px;
}

.fa-input-row .fa-input-iconbtn {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ced4da;
  border-radius: 8px;
  background: #fff;
  color: #555;
  padding: 0;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease, border-color 120ms ease;
}
.fa-input-row .fa-input-iconbtn:hover {
  background: #f4f6f8;
  color: #2c3440;
  border-color: #adb5bd;
}
.fa-input-row .fa-input-iconbtn:focus-visible {
  outline: 2px solid #4a90d9;
  outline-offset: 2px;
}

.fa-input-row .fa-send-btn {
  flex: 0 0 auto;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: #007ac7;
  color: white;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: background-color 120ms ease;
}
.fa-input-row .fa-send-btn:hover:not(:disabled) {
  background: #0369a8;
}
.fa-input-row .fa-send-btn:disabled {
  background: #a9c3d6;
  cursor: not-allowed;
}
.fa-input-row .fa-send-btn:focus-visible {
  outline: 2px solid #4a90d9;
  outline-offset: 2px;
}

.fa-attachment-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 4px;
  padding: 6px 10px;
  max-width: 100%;
}

.fa-attachment-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: #666;
}

.fa-attachment-failed-icon {
  font-weight: 800;
  color: #c82333;
}

.fa-attachment-queued-icon {
  color: #999;
}

.fa-attachment-file-icon {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.fa-attachment-logo {
  width: 20px;
  height: 20px;
  display: inline-block;
}

.fa-attachment-logo.pdf {
  color: #c62828;
}

.fa-attachment-logo.docx {
  color: #1565c0;
}

.fa-attachment-logo.txt {
  color: #455a64;
}

.fa-attachment-logo.doc {
  color: #6c757d;
}

.fa-attachment-name {
  font-size: 12px;
  color: #333;
  min-width: 0;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fa-attachment-right {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
}

.fa-badge {
  --progress: 0%;
  --badge-color: #6c757d;
  --badge-bg: #e9ecef;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  min-height: 20px;
  font-size: 11px;
  line-height: 14px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid var(--badge-color);
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.08) var(--progress), var(--badge-bg) var(--progress));
  color: #333;
  white-space: nowrap;
}

.fa-badge.upload {
  --badge-color: #28a745;
  --badge-bg: #e9f6ee;
  color: #155724;
  background: linear-gradient(90deg, rgba(40, 167, 69, 0.25) var(--progress), var(--badge-bg) var(--progress));
}

.fa-badge.processing {
  --badge-color: #1e7eeb;
  --badge-bg: #e7f1ff;
  color: #0b3d91;
  background: var(--badge-bg);
}

.fa-badge.pending {
  --badge-color: #fd7e14;
  --badge-bg: #fff4e5;
  --pulse-color: rgba(253, 126, 20, 0.35);
  color: #7a3e00;
  background: var(--badge-bg);
}

.fa-badge.failed {
  --badge-color: #b00020;
  --badge-bg: #fde8ea;
  color: #b00020;
}

.fa-badge.disabled {
  --badge-color: #b58100;
  --badge-bg: #fff4d1;
  color: #7a5400;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  gap: 6px;
}
.fa-badge.disabled .fa {
  font-size: 11px;
}
/* inline-SVG icon (parking P variants) used inside the paused badge */
.callout-paused-icon {
  width: 1em;
  height: 1em;
  display: inline-block;
  vertical-align: -0.15em;
}

/* over-limit callout message — styled as a chat bubble matching the shape
   AND typography of regular assistant messages (inherits the FA widget's
   16px / 24px font, same word-wrap behavior, border-radius 18px).
   sits inside .fa-assistant-msg-wrap so it inherits the chat bubble max-width;
   timestamp is rendered as a sibling below via .fa-meta-row.
   mirrors the Uploads Disabled banner pattern in help_center.vue (to be
   extracted into a shared Callout Panel primitive during NC Phase 2). */
.fa-limit-callout {
  background-color: #fdf6e3;
  border: 1px solid #f0e4c2;
  border-radius: 18px;
  padding: 14px 18px;
  color: #6b5a1e;
  word-wrap: break-word;
}
.fa-limit-callout-header {
  margin-bottom: 8px;
}
.fa-limit-callout p {
  margin: 0 0 10px 0;
}
.fa-limit-callout-action {
  margin-top: 6px;
}

/* callout layout: profile-card style.
   row 1: anchor icon + badge on the same line, vertically centered.
   row 2: body copy and actions, full width, left-aligned with the icon start.
   mirrors help_center.vue (to be extracted into shared Callout Panel during NC Phase 2). */
.callout-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.callout-anchor-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  color: #b58100;
}
.callout-body {
  /* full width, no indent — content starts at the icon's left edge */
}

/* callout CTA button — dark-amber fill on cream panel, echoes the badge text color.
   ~6.7:1 contrast on white text, WCAG AA/AAA pass. Matches help_center.vue. */
.callout-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #7a5400;
  color: #fff;
  border: 1px solid #7a5400;
  border-radius: 4px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  cursor: pointer;
  transition: background-color 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease;
}
.callout-cta-btn:hover {
  background: #5f4100;
  border-color: #5f4100;
}
.callout-cta-btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(122, 84, 0, 0.25);
}
.callout-cta-btn:active {
  background: #4a3200;
  border-color: #4a3200;
}

.fa-badge.processing.pulsing {
  --pulse-color: rgba(30, 126, 235, 0.35);
}

.fa-badge.pulsing {
  animation: faBadgePulse 1.2s ease-in-out infinite;
}

@keyframes faBadgePulse {
  0% {
    box-shadow: inset 0 0 0 0 var(--pulse-color);
    opacity: 0.85;
  }
  50% {
    box-shadow: inset 0 0 6px 3px var(--pulse-color);
    opacity: 1;
  }
  100% {
    box-shadow: inset 0 0 0 0 var(--pulse-color);
    opacity: 0.85;
  }
}

.fa-attachment-pill.status-uploaded {
  border-color: steelblue;
  background: #fff;
}

.fa-attachment-pill.status-uploaded .fa-attachment-icon {
  color: steelblue;
}

.fa-attachment-pill.status-failed {
  border-color: #dc3545;
  background: #fff;
}

.fa-attachment-pill.status-uploading {
  border-color: #28a745;
  background: #fff;
}

.fa-attachment-pill.status-processing {
  border-color: #1e7eeb;
  background: #fff;
}

.fa-attachment-remove {
  border: none;
  background: transparent;
  padding: 0 4px;
  font-size: 16px;
  line-height: 1;
  color: #777;
}

.fa-attachment-remove:disabled {
  opacity: 0.4;
}


.message-timestamp {
  font-size: 11px;
  color: #999;
}

.fa-assistant-msg-wrap {
  display: inline-block;
  max-width: 70%;
}

.fa-meta-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  padding: 0 4px;
}

.fa-rating-controls {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
}

.fa-rate-btn {
  background: none;
  border: none;
  color: #bbb;
  cursor: pointer;
  font-size: 14px;
  padding: 2px 5px;
  transition: color 0.15s;
}

.fa-rate-btn:hover {
  color: #007ac7;
}

.fa-rate-btn.active {
  color: #007ac7;
}

.fa-rate-btn.concern:hover {
  color: #dc3545;
}

.fa-rate-btn.concern.active {
  color: #dc3545;
}

.fa-rated-thanks {
  color: #6c757d;
  font-size: 12px;
  margin-left: 6px;
  align-self: center;
}

.fa-rating-comment {
  width: 100%;
  margin-top: 6px;
}

.thinking-indicator {
  display: inline-flex;
  align-items: center;
  background-color: #e9ecef;
  color: #333;
  padding: 10px 15px;
  border-radius: 18px;
  gap: 8px;
}

.markdown-content {
  text-align: left;
}

.markdown-content strong {
  font-weight: bold;
}

.markdown-content em {
  font-style: italic;
}

.markdown-content ol,
.markdown-content ul {
  margin: 8px 0;
  padding-left: 20px;
}

.markdown-content li {
  margin: 4px 0;
  line-height: 1.4;
}

.markdown-content ol {
  list-style-type: decimal;
}

.markdown-content ul {
  list-style-type: disc;
}

/* nested list styling */
.markdown-content ul ul,
.markdown-content ol ul {
  margin: 4px 0;
  padding-left: 18px;
  list-style-type: circle;
}

.markdown-content ul ul ul,
.markdown-content ol ul ul {
  list-style-type: square;
}

.markdown-content ul ol,
.markdown-content ol ol {
  margin: 4px 0;
  padding-left: 18px;
}

.markdown-content br {
  line-height: 1.6;
}

.markdown-content .md-link {
  color: #0068a8;
  text-decoration: none;
  cursor: pointer;
  word-break: break-word;
}

.markdown-content .md-link:hover {
  text-decoration: underline;
  color: #004a7c;
}

.markdown-content .md-link-nav {
  font-weight: 500;
}

.markdown-content .md-link-help {
  font-style: italic;
}
</style>

<style>
/* tutorial-mode halo on the FA input while animated typing is in progress.
   unscoped because b-form-input renders a native <input class="form-control">,
   which scoped styles don't reliably reach. */
.fa-input-tutorial-glow.form-control,
.fa-input-tutorial-glow.form-control:focus {
  border-color: #a29bfe !important;
  animation: fa-input-tutorial-pulse 1.1s cubic-bezier(0.4, 0, 0.2, 1) infinite alternate;
}

@keyframes fa-input-tutorial-pulse {
  from {
    box-shadow:
      0 0 0 3px rgba(162, 155, 254, 0.32),
      0 0 12px 2px rgba(72, 219, 251, 0.4);
  }
  to {
    box-shadow:
      0 0 0 4px rgba(254, 202, 87, 0.42),
      0 0 18px 5px rgba(255, 107, 107, 0.38);
  }
}

/* row-level highlight variants applied during tutorial typing. mirror the
   launcher's chase/ripple implementations so a user's selected highlight
   style reads consistently across both control surfaces.

   the chase variant uses @property --fa-chase-angle to sweep a conic-gradient
   rainbow arc around the row's perimeter without rotating the ring itself
   (which was the "rectangle spinning on screen" bug on the pill-shaped
   launcher button). ripple uses two staggered expanding borders. both sit on
   pseudo-elements so they can extend past the row's flex content without
   affecting layout.

   property has to be registered here too even though it's also registered
   in the launcher component — scoped styles are still parsed per-component,
   and identical @property declarations are safe (spec treats them as the
   same registration). */
@property --fa-chase-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}

.fa-input-row {
  /* positioned context so the tutorial pseudo-elements anchor to the row */
  position: relative;
}

/* chase — continuous LED-chaser rainbow wrapping the row's edge. mirrors
   the launcher: 12 stops per cycle (6 rainbow anchors + 6 midpoints) for
   a smooth continuous rainbow, six full loops around the ring so density
   reads the same regardless of control size. */
.fa-input-row--tutorial-chase::before {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: 14px;
  padding: 4px;
  background: repeating-conic-gradient(
    from var(--fa-chase-angle),
    #ff2d6d   0deg,
    #ff5a3a   5deg,
    #ff8a1f  10deg,
    #ffb820  15deg,
    #ffe21f  20deg,
    #8fdd4a  25deg,
    #2bd96d  30deg,
    #2bd3bd  35deg,
    #1e9bff  40deg,
    #6a6dff  45deg,
    #b94dff  50deg,
    #ff3db9  55deg,
    #ff2d6d  60deg
  );
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
          mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
          mask-composite: exclude;
  filter: saturate(1.25) drop-shadow(0 0 5px rgba(255, 255, 255, 0.45));
  animation: fa-input-chase-sweep 1.2s linear infinite;
  pointer-events: none;
  z-index: 0;
}
@keyframes fa-input-chase-sweep {
  to { --fa-chase-angle: 60deg; }
}

.fa-input-row--tutorial-ripple::before,
.fa-input-row--tutorial-ripple::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 10px;
  border: 3px solid rgba(72, 150, 220, 0.7);
  animation: fa-input-ripple 1.6s ease-out infinite;
  pointer-events: none;
  z-index: 0;
}
.fa-input-row--tutorial-ripple::after {
  animation-delay: 0.8s;
}
@keyframes fa-input-ripple {
  /* smaller scale than the launcher ripple because the row is much wider —
     at 1.9x scale the rings would run off the panel edges. 1.12 reads as a
     clear "ping" without overflow. */
  0%   { transform: scale(1);    opacity: 0.75; border-width: 3px; }
  100% { transform: scale(1.12); opacity: 0;    border-width: 1px; }
}

/* MAGENTA variant for the textarea — single-color pulse, mirrors the same
   breath-cycle pattern as the launcher's magenta glow (see fleet_assistant_
   launcher.vue keyframes). applies to the textarea itself rather than the
   row because, like pulse, it's a box-shadow halo rather than a perimeter
   pseudo-element. */
.fa-input-tutorial-magenta.form-control,
.fa-input-tutorial-magenta.form-control:focus {
  border-color: #d500f9 !important;
  animation: fa-input-tutorial-magenta-breathe 1.4s ease-in-out infinite;
}
@keyframes fa-input-tutorial-magenta-breathe {
  0%, 100% {
    box-shadow:
      0 0 0 2px rgba(213, 0, 249, 0.35),
      0 0 14px 3px rgba(213, 0, 249, 0.30);
  }
  50% {
    box-shadow:
      0 0 0 4px rgba(255, 77, 255, 0.55),
      0 0 24px 8px rgba(213, 0, 249, 0.50);
  }
}

/* AURORA variant on the row — slow drifting wash of soft greens, teals,
   and purples around the input perimeter. same aesthetic as the launcher's
   aurora (see fleet_assistant_launcher.vue), palette mirrored identically
   so the two surfaces read as the same feature. */
@property --fa-aurora-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}
.fa-input-row--tutorial-aurora::before,
.fa-input-row--tutorial-aurora::after {
  content: '';
  position: absolute;
  inset: -8px;
  border-radius: 16px;
  padding: 5px;
  background: conic-gradient(
    from var(--fa-aurora-angle),
    rgba(120, 255, 200, 0.55)    0deg,
    rgba(100, 220, 240, 0.55)   80deg,
    rgba(160, 180, 255, 0.55)  150deg,
    rgba(210, 140, 255, 0.55)  220deg,
    rgba(140, 220, 200, 0.55)  290deg,
    rgba(120, 255, 200, 0.55)  360deg
  );
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
          mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
          mask-composite: exclude;
  filter: blur(4px);
  pointer-events: none;
  z-index: 0;
}
.fa-input-row--tutorial-aurora::before {
  animation:
    fa-input-aurora-drift 5s linear infinite,
    fa-input-aurora-breath 2.4s ease-in-out infinite alternate;
}
.fa-input-row--tutorial-aurora::after {
  /* offset phase so the two layers never darken together */
  animation:
    fa-input-aurora-drift 5s linear -2.5s infinite reverse,
    fa-input-aurora-breath 2.4s ease-in-out -1.2s infinite alternate-reverse;
}
@keyframes fa-input-aurora-drift {
  to { --fa-aurora-angle: 360deg; }
}
@keyframes fa-input-aurora-breath {
  from { opacity: 0.45; }
  to   { opacity: 1.00; }
}

.markdown-content h1,
.markdown-content h2,
.markdown-content h3,
.markdown-content h4,
.markdown-content h5,
.markdown-content h6 {
  margin: 12px 0 8px 0;
  line-height: 1.3;
  font-weight: 600;
}

.markdown-content h1 {
  font-size: 1.15em;
}

.markdown-content h2 {
  font-size: 1.1em;
}

.markdown-content h3 {
  font-size: 1.0em;
}

.markdown-content h4,
.markdown-content h5,
.markdown-content h6 {
  font-size: 0.95em;
}

.markdown-content {
  padding-bottom: 8px !important;
}

.markdown-content p {
  margin: 0 0 8px 0;
}

.markdown-content p:last-child {
  margin-bottom: 0 !important;
}

.markdown-content ul:last-child,
.markdown-content ol:last-child {
  margin-bottom: 0 !important;
}

.markdown-content code {
  font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 0.92em;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 1px 4px;
}

.markdown-content pre {
  font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 12px;
  line-height: 1.4;
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 10px;
  overflow-x: auto;
}

.markdown-content pre code {
  background: transparent;
  border: none;
  padding: 0;
}

.markdown-content table.markdown-table {
  width: 100%;
  border-collapse: collapse;
  margin: 10px 0;
}

.markdown-content table.markdown-table th,
.markdown-content table.markdown-table td {
  border: 1px solid #d9d9d9;
  padding: 6px 8px;
  vertical-align: top;
}

.markdown-content table.markdown-table th {
  background: #f7f7f7;
  font-weight: 600;
}

.markdown-content .md-inline-img {
  height: 1em;
  width: auto;
  vertical-align: middle;
  margin: 0 3px;
}
</style>
